import { forwardRef, useId } from "react";
import type { ReactNode, TextareaHTMLAttributes } from "react";
import clsx from "clsx";
import type { FieldLabelVariant } from "./Field.js";
import styles from "./Field.module.scss";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Mono uppercase label. */
  label: string;
  /** A quiet word after the label, in body type at normal case and weight — "Optional". It is part of the
   *  label, so it is read with the field's name. */
  labelAside?: ReactNode;
  /** Keep the label for screen readers only — when the card title already names the control. */
  hideLabel?: boolean;
  /** ADDITIVE error state: danger border + danger-ink caption wired via aria-describedby. */
  error?: string;
  hint?: ReactNode;
  /** `caption` (default) — mono, tiny, tracked; the shipped majority. `heading` — Inter, a step larger,
   * full ink, matching a section label: for a surface that is ONE group of titled fields. */
  labelVariant?: FieldLabelVariant;
  /** Guidance shown BETWEEN the label and the control — what to type. Use `hint` for a note that belongs
   * UNDER the control instead. */
  subLabel?: ReactNode;
}

/**
 * The multi-line sibling of {@link Field} — same label, same paper fill, same 1.5px border and accent
 * focus ring, same gray disabled state, because it shares `.control`. It exists because `Field` is
 * `<input>`-only, which is why **26 `.textarea` rules** had been hand-rolled across the app.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, labelAside, labelVariant, subLabel, hideLabel, error, hint, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const subId = `${fieldId}-sub`;
  const describedBy = [error ? errorId : null, hint ? hintId : null, subLabel ? subId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={clsx(styles.field, error && styles.hasError, className)}>
      <label htmlFor={fieldId} className={hideLabel ? styles.srOnly : styles.label} data-variant={labelVariant}>
        {label}
        {labelAside != null && <span className={styles.labelAside}> {labelAside}</span>}
      </label>
      {subLabel && (
        <p id={subId} className={styles.subLabel}>
          {subLabel}
        </p>
      )}
      <textarea
        ref={ref}
        id={fieldId}
        className={clsx(styles.control, styles.textarea)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        // A textarea is free text by definition, so it takes `Field`'s default with no type test to make
        // (PO, 2026-09-08): the browser has nothing to offer a note, a bio or a pasted itinerary but the
        // reader's own text from another form. BEFORE `rest`, so a caller can still say otherwise.
        // `spellCheck` is deliberately untouched — this is the longest prose in the app.
        autoComplete="off"
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
